<template>
  <section v-if="!is_supplier" class="section-container">
    <div class="content-wrapper">
      <div v-if="detail.id" class="content-heading">
        {{ $t('detail.pre-order-edit') }}
      </div>
      <div v-else class="content-heading">
        {{ $t('detail.pre-order-add') }}
      </div>
      <div class="row">
        <div class="col-xl-4">
          <div class="card card-default">
            <div class="card-body">
              <form class="form-modal form-detail" @submit.prevent="save">

                <div class="form-group">
                  <label for="code">{{ $t('detail.code') }} *</label>
                  <multiselect
                      id="code"
                      v-model="detail.part"
                      :class="{'has-error': errors.part}"
                      :options="codes"
                      :placeholder="$t('detail.select')"
                      :searchable="true"
                      :show-no-results="false"
                      :disabled="is_from_bc"
                      label="code_with_name"
                      track-by="id"
                      @select="select_code"
                      @search-change="suggest_code">
                    <template slot="noOptions">{{ $t('global.no-options') }}</template>
                  </multiselect>
                  <ul v-if="errors.part" class="parsley-errors-list filled">
                    <li class="parsley-required">{{ errors.part[0] }}</li>
                  </ul>
                </div>

                <div class="form-group">
                  <label>BC:</label>
                  <div v-if="is_from_bc">
                    <b-c-detail-status
                      justify-content="left"
                      :detail="detail"
                    />
                  </div>
                </div>

                <div class="form-group">
                  <label>{{ $t('details.order_number') }}:</label>
                  <div>{{ detail.order_number }}</div>
                </div>

                <div class="form-group">
                  <label>{{ $t('details.transport-nr') }}:</label>
                  <div>{{ detail.customer_number }}</div>
                </div>

                <div class="form-group">
                  <label>{{ $t('details.customer_name') }}:</label>
                  <div>{{ detail.customer_name }}</div>
                </div>

                <div class="form-group">
                  <label>{{ $t('details.creator') }}:</label>
                  <div>{{ detail.creator_name }}</div>
                </div>

                <div class="form-group">
                  <label>{{ $t('detail.name') }}:</label>
                  <div>{{ name }}</div>
                </div>

                <div class="form-group">
                  <label>{{ $t('detail.categories') }}: </label>
                  <div>{{ detail.category_path }}</div>
                </div>

                <div class="form-group">
                  <label>{{ $t('detail.productclass') }}: </label>
                  <div> {{ detail.part_type_name }}</div>
                </div>

                <div class="form-group">
                  <label>{{ $t('detail.unit') }}: </label>
                  <div> {{ detail.part_unit_code }}</div>
                </div>

                <div class="form-group">
                  <label for="replenishment_qty">{{ $t('detail.replenishment-qty') }}</label>
                  <input
                      id="replenishment_qty"
                      v-model="detail.replenishment_qty"
                      :class="{'parsley-error': errors.replenishment_qty}"
                      :placeholder="$t('detail.needed-qty')"
                      autocomplete="off"
                      class="form-control"
                      type="number"
                      min="0"
                      @input="clear_error"
                  >
                  <ul v-if="errors.replenishment_qty" class="parsley-errors-list filled">
                    <li class="parsley-required">{{ errors.replenishment_qty[0] }}</li>
                  </ul>
                </div>

                <div class="form-group">
                  <label for="warehouse">{{ $t('detail.warehouse') }} *</label>
                  <multiselect
                      id="warehouse"
                      v-model="detail.warehouse"
                      :class="{'has-error': errors.warehouse}"
                      :options="warehouses"
                      :placeholder="$t('detail.select-warehouse')"
                      :disabled="is_from_bc"
                      label="name"
                      track-by="id"
                      @input="clear_error"
                  >
                    <template slot="noOptions">{{ $t('global.no-options') }}</template>
                  </multiselect>
                  <ul v-if="errors.warehouse" class="parsley-errors-list filled">
                    <li class="parsley-required">{{ errors.warehouse[0] }}</li>
                  </ul>
                </div>

                <buttons url="details"/>

              </form>
            </div>
          </div>
        </div>
        <div v-if="detail.part" class="col-xl-8">
          <div class="card card-default">
            <div class="card-body">
              <label>{{ $t('detail.files') }}</label>
              <uploader
                  :options="options"
                  class="uploader"
                  @file-success="onFileSuccess">
                <uploader-unsupport></uploader-unsupport>
                <uploader-drop>
                  <p>{{ $t('detail.drop-files-or-upload') }}</p>
                  <uploader-btn :attrs="attrs">{{ $t('detail.choose-files') }}</uploader-btn>
                </uploader-drop>
                <div v-if="documents.length" class="uploader-list">
                  <div v-for="(row, index) in documents" :key="row.uuid">
                    <div class="uploader-file">
                      <div class="uploader-file-info">
                        <div class="uploader-file-name">
                          <em class="icon-doc"></em>
                          <span>
                            <a :href="row.src">{{ row.description }}</a>
                          </span>
                        </div>
                        <div class="uploader-file-size">
                          {{ picture_size(row.size) }}
                        </div>
                        <div class="uploader-file-actions">
                          <span
                              class="uploader-file-remove"
                              @click="delete_file(index, row.uuid)"
                          ></span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-if="images.length" class="uploader-list">
                  <silent-box :gallery="images">
                    <template #silentbox-item="{ silentboxItem }">
                      <div class="uploader-file">
                        <div class="uploader-file-info">
                          <div class="uploader-file-name">
                            <img
                                class="img-thumbnail"
                                :src="silentboxItem.thumbnail"
                            />
                            <span
                                class="silentbox-item"
                                :description="silentboxItem.description"
                                :src="silentboxItem.src"
                            >{{ silentboxItem.description }}
                            </span>
                          </div>
                          <div class="uploader-file-size">
                            {{ picture_size(silentboxItem.size) }}
                          </div>
                          <div class="uploader-file-actions">
                            <span
                                class="uploader-file-remove"
                                @click.stop="delete_file(
                                  silentboxItem.index,
                                  silentboxItem.uuid
                                )"
                            ></span>
                          </div>
                        </div>
                      </div>
                    </template>
                  </silent-box>
                </div>
              </uploader>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import api from '@/api'
import {objectToFormData, parseApiError, is_image, get_file} from '@/utils'
import Buttons from '@/components/Buttons.vue'
import message from '@/message'
import More from '@/components/More.vue'
import Multiselect from 'vue-multiselect'
import axios from 'axios'
import Gallery from '@/components/Gallery.vue'
import BCDetailStatus from "@/components/BCDetailStatus.vue";

const getData = (id, next) => {
  if (id) {
    Promise.all([
      api.get(`/details/${id}/`),
      api.get('/warehouses/?is_active=true&page_size=0'),
      api.get(`/units/?page_size=0`)
    ])
        .then(([detail, warehouses, units]) => {
          let images = [];
          let documents = [];

          detail.data.part_files_data.forEach((v, i) => {
            let data = {
              src: get_file(v.uuid),
              thumbnail: process.env.FSS_URL.concat("thumbnail/40/40/", v.uuid),
              thumbnailHeight: 40,
              thumbnailWidth: 40,
              description: v.file_name,
              alt: v.file_name,
              type: v.file_type,
              size: v.file_size,
              uuid: v.uuid,
              index: i,
            }

            if (is_image(v.file_type)) {
              images.push(data);
            } else {
              documents.push(data);
            }
          });

          let active_units = []

          units.data.forEach(u => {
                if (u.is_active) {
                  active_units.push({
                    id: u.id,
                    name: u.name,
                  })
                } else {
                  if (u.id === detail.data.part_unit) {
                    active_units.push({
                      id: u.id,
                      name: u.name,
                      $isDisabled: true
                    })
                  }
                }
              }
          )

          if (detail.data.part_unit) {
            detail.data.part_unit = {
              id: detail.data.part_unit,
              name: detail.data.part_unit_code,
            }
          }

          detail.data.part = {
            'id': detail.data.part,
            'code': detail.data.part_code,
            'code_with_name': detail.data.part_code + ' | ' + detail.data.part_name,
            'category_path': detail.data.category_path,
            'part_type_name': detail.data.part_type_name,
            'files': detail.data.part.files,
            'files_data': detail.data.part.files_data
          }

          detail.data.warehouse = {
            'id': detail.data.warehouse,
            'name': detail.data.warehouse_name
          }


          next({
            name: detail.data.part_name,
            detail: detail.data,
            warehouses: warehouses.data,
            images: images,
            documents: documents,
            units: active_units,
          })
        })
  } else {
    Promise.all([
      api.get('/warehouses/?is_active=true&page_size=0'),
      api.get(`/units/?is_active=true&page_size=0`)
    ])
        .then(([warehouses, units]) => {
          let active_units = units.data
              .map(u => {
                return {
                  id: u.id,
                  name: u.name,
                }
              });

          next({
            detail: {
              part: null,
              replacement_qty: null,
              replenishment_qty: null,
              status: 'PREPARED',
              warehouse: null
            },
            warehouses: warehouses.data,
            units: active_units,
          })
        })
  }
}

export default {
  components: {
    BCDetailStatus,
    buttons: Buttons,
    more: More,
    Multiselect,
    silentBox: Gallery,
  },
  data() {
    return {
      detail: {},
      name: null,
      codes: [],
      errors: {},
      more: null,
      warehouses: [],
      images: [],
      documents: [],
      units: [],
      options: {
        target: process.env.FSS_URL.concat('chunk_upload'),
        singleFile: true,
        testChunks: false,
        chunkSize: 500000,
        withCredentials: true,
        headers: () => {
          // Return as function, so there is up-to-date token from localStorage
          return {'Authorization': `Token ${localStorage.getItem('etend.token')}`}
        },
        generateUniqueIdentifier: function () {
          return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            var r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8)
            return v.toString(16)
          })
        },
        query: function (file, chunk, test) {
          return {
            dzuuid: file.uniqueIdentifier,
            dzchunkindex: chunk.offset,
            dztotalchunkcount: file.chunks.length,
            dztotalfilesize: file.size
          }
        }
      },
      attrs: {
        accept: [
          '.jpg',
          '.jpeg',
          '.png',
          '.doc',
          '.docx',
          '.xls',
          '.xlsx',
          '.pdf',
          '.odt',
          '.ppt',
          '.pptx',
          '.csv',
          '.txt',
          '.rtf',
          '.cdr',
          '.ai',
          '.eps',
          '.drw',
          '.dwg',
          '.dgn'
        ]
      }
    }
  },
  computed: {
    is_supplier() {
      return this.$store.getters.user_group === 'Supplier'
    },
    is_from_bc() {
      return !!this.detail.bc_id
    }
  },
  mounted() {
    this.$parent.menu = 'details'
    this.$parent.submenu = ''
    this.$i18n.locale = this.$store.getters.language
  },
  methods: {
    is_image(file_type) {
      if (file_type === 'image/jpeg') {
        return true
      }
      if (file_type === 'image/jpg') {
        return true
      }
      if (file_type === 'image/png') {
        return true
      }
      if (file_type === 'image/gif') {
        return true
      }
      if (file_type === 'image/bmp') {
        return true
      }
      if (file_type === 'image/tiff') {
        return true
      }
      return false
    },
    get_file(uuid) {
      return process.env.FSS_URL.concat('get/', uuid)
    },
    get_thumbnail(uuid) {
      return process.env.FSS_URL.concat('thumbnail/20/20/', uuid)
    },
    delete_file(index, uuid) {
      this.$swal({
        type: 'warning',
        title: this.$i18n.t('detail.are-you-sure'),
        text: this.$i18n.t('detail.are-you-sure-text-cancel'),
        showCancelButton: true
      })
          .then((choice) => {
            if (choice.value) {
              this.detail.part.files_data.splice(index, 1)
              this.detail.part.files.splice(index, 1)
              api.patch(`/parts/${this.detail.part.id}/`, objectToFormData({files: this.detail.part.files}))
                  .catch(error => {
                    let errors = parseApiError(error, this.$i18n)
                    if (error.response && error.response.status === 400) {
                      this.errors = errors
                    } else {
                      message.error(errors)
                    }
                  })
              axios.delete(process.env.FSS_URL.concat('delete/', uuid))

              this.images = this.images.filter(i => i.uuid !== uuid);
              this.documents = this.documents.filter(i => i.uuid !== uuid);
            }
          })
    },
    picture_size(bytes) {
      let decimals = 2
      if (bytes === 0) {
        return '0 Bytes'
      }
      const k = 1024
      const dm = decimals < 0 ? 0 : decimals
      const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
    },
    onFileSuccess(rootFile, file, message, chunk) {
      const formData = new FormData()
      formData.append('dzuuid', file.uniqueIdentifier)
      formData.append('filename', file.name)
      formData.append('dztotalchunkcount', file.chunks.length)
      formData.append('dztotalfilesize', file.size)
      formData.append('access', 'public')
      axios.post(process.env.FSS_URL.concat('chunks_done'), formData)
          .then(response => {

            if (!this.detail.part.files_data) {
              this.detail.part.files_data = []
            }
            this.detail.part.files_data.push({
              id: response.data.uuid,
              uuid: response.data.uuid,
              file_name: response.data.fileName,
              file_size: response.data.fileSize,
              file_type: response.data.fileType
            })
            if (!this.detail.part.files) {
              this.detail.part.files = []
            }
            this.detail.part.files.push(response.data.uuid)

            let data = {
              src: get_file(response.data.uuid),
              thumbnail: process.env.FSS_URL.concat("thumbnail/40/40/", response.data.uuid),
              thumbnailHeight: 40,
              thumbnailWidth: 40,
              description: response.data.fileName,
              alt: response.data.fileName,
              type: response.data.fileType,
              size: response.data.fileSize,
              uuid: response.data.uuid
            };
            if (is_image(response.data.fileType)) {
              this.images.push(data);
            } else {
              this.documents.push(data);
            }
            api.patch(`/parts/${this.detail.part.id}/`, objectToFormData({files: this.detail.part.files}))
                .catch(error => {
                  let errors = parseApiError(error, this.$i18n)
                  if (error.response && error.response.status === 400) {
                    this.errors = errors
                  } else {
                    message.error(errors)
                  }
                })
          })
    },
    select_code(code) {
      this.name = code.name
      this.detail['part_type_name'] = code.part_type_name
      this.detail['category_path'] = code.category_path

      let input_data = null
      if (code.unit_code) {
        input_data = {
          id: code.unit,
          name: code.unit_code
        }
      }
      // this.$refs.units_multiselect.$emit("input", input_data)
      if ('part' in this.errors) {
        delete this.errors['part']
      }
      if ('unit' in this.errors) {
        delete this.errors['unit']
      }
    },
    suggest_code(q) {
      if (q.length > 4) {
        api.get('/parts/?is_active=true&page_size=5000', {
          params: {
            code__icontains: q
          }
        })
            .then(r => {
              let codes = []
              for (var num in r.data.results) {
                let resp = r.data.results[num]
                resp['code_with_name'] = resp.code + ' | ' + resp.name
                codes.push(resp)
              }
              this.codes = codes
            })
      }
    },
    save() {
      this.errors = {}

      if (!this.detail.part || !this.detail.part.id) {
        this.errors['part'] = [this.$i18n.t('detail.empty-code')]
      }
      if (!this.detail.replacement_qty) {
        if (!this.detail.replenishment_qty) {
          this.errors['replacement_qty'] = [this.$i18n.t('detail.empty-qty')]
        }
      }
      if (!this.detail.replenishment_qty && !this.detail.replacement_qty) {
        this.errors['replenishment_qty'] = [this.$i18n.t('detail.empty-qty')]
      }
      if (!this.detail.warehouse) {
        this.errors['warehouse'] = [this.$i18n.t('detail.empty-warehouse')]
      }
      if (Object.keys(this.errors).length > 0) {
        return
      }

      let data = {
        id: this.detail.id,
        part: this.detail.part ? this.detail.part.id : null,
        replacement_qty: this.detail.replacement_qty ? this.detail.replacement_qty : 0,
        replenishment_qty: this.detail.replenishment_qty ? this.detail.replenishment_qty : 0,
        qty: this.detail.replenishment_qty ? this.detail.replenishment_qty : 0,
        status: 'PREPARED',
        warehouse: this.detail.warehouse ? this.detail.warehouse.id : null
      }

      let part_data = {
        unit: this.detail.part_unit.id,
        unit_code: this.detail.part_unit.code
      }

      if (this.detail.id) {
        Promise.all([
          api.patch(`/details/${this.detail.id}/`, data),
          api.patch(`/parts/${this.detail.part.id}/`, part_data)
        ])
            .then(() => {
              message.success(this.$i18n.t('detail.updated'))
              this.$router.push({name: 'details'})
            })
            .catch(error => {
              let errors = parseApiError(error, this.$i18n)
              if (error.response && error.response.status === 400) {
                this.errors = errors
              } else {
                message.error(errors)
              }
            })
      } else {
        Promise.all([
          api.post('/details/', data),
          api.patch(`/parts/${this.detail.part.id}/`, part_data)
        ])
            .then(() => {
              message.success(this.$i18n.t('detail.added'))
              if (this.more) {
                this.detail = {
                  part: null,
                  replacement_qty: null,
                  replenishment_qty: null,
                  status: 'PREPARED',
                  warehouse: null
                }
                this.name = ''
                this.$router.push({name: 'detail_add'})
              } else {
                this.$router.push({name: 'details'})
              }
            })
            .catch(error => {
              let errors = parseApiError(error, this.$i18n)
              if (error.response && error.response.status === 400) {
                this.errors = errors
              } else {
                message.error(errors)
              }
            })
      }
    },
    clear_error(value, id) {
      if (value.target && !id) {
        id = value.target.id
      }
      if (value && id in this.errors) {
        delete this.errors[id]
      }
      let qty = (id === 'replenishment_qty') ? 'replacement_qty' : 'replenishment_qty'
      if (qty in this.errors) {
        delete this.errors[qty]
      }
    }
  },
  beforeRouteEnter(to, from, next) {
    getData(to.params.id, data => {
      next(vm => {
        Object.assign(vm.$data, data)
      })
    })
  },
  beforeRouteUpdate(to, from, next) {
    getData(to.params.id, data => {
      Object.assign(this.$data, data)
      next()
    })
  }
}
</script>

<style lang="css" scoped>
.has-error >>> .multiselect__tags {
  border-color: #f05050 !important;
}
</style>



// WEBPACK FOOTER //
// src/views/Detail.vue